import Button from "@mui/material/Button";
import { PaletteColor, styled } from "@mui/material/styles";

import { MDButtonProps } from ".";

type Colored = {
  primary: string;
  secondary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
  light: string;
  dark: string;
};

interface OwnerState {
  color?:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
    | "white";
  variant?: "text" | "contained" | "outlined" | "gradient";
  size?: "small" | "medium" | "large";
  circular?: boolean;
  iconOnly?: boolean;
  darkMode?: boolean;
}

export default styled(Button, {
  shouldForwardProp: (prop) => prop !== "ownerState" && prop !== "isLoading",
})<MDButtonProps & { ownerState: OwnerState }>(({ theme, ownerState }) => {
  const { palette, functions, borders, boxShadows } = theme;

  const {
    color = "white",
    variant = "contained",
    size = "medium",
    circular = false,
    iconOnly = false,
    darkMode = false,
  } = ownerState;

  const { white, text, gradients, grey, transparent } = palette;
  const { boxShadow, linearGradient, pxToRem, rgba } = functions;
  const { borderRadius } = borders;
  const { colored } = boxShadows;

  const containedStyles = () => {
    const paletteColor = palette[color as keyof typeof palette] as PaletteColor;
    const backgroundValue = paletteColor?.main ?? white.main;
    const focusedBackgroundValue = paletteColor?.focus;
    const boxShadowValue = colored[color as keyof Colored]
      ? `${boxShadow([0, 3], [3, 0], paletteColor?.main, 0.15)}, ${boxShadow(
          [0, 3],
          [1, -2],
          paletteColor?.main,
          0.2
        )}, ${boxShadow([0, 1], [5, 0], paletteColor?.main, 0.15)}`
      : "none";
    const hoveredBoxShadowValue = colored[color as keyof Colored]
      ? `${boxShadow([0, 14], [26, -12], paletteColor?.main, 0.4)}, ${boxShadow(
          [0, 4],
          [23, 0],
          paletteColor?.main,
          0.15
        )}, ${boxShadow([0, 8], [10, -5], paletteColor?.main, 0.2)}`
      : "none";

    let colorValue = white.main;
    if (!darkMode && (color === "white" || color === "light" || !paletteColor)) {
      colorValue = text.main || white.main;
    } else if (darkMode && (color === "white" || color === "light" || !paletteColor)) {
      colorValue = grey[600];
    }

    let focusedColorValue = paletteColor?.focus ?? white.main;
    if (color === "white") {
      focusedColorValue = text.main as string;
    } else if (["primary", "error", "dark"].includes(color)) {
      focusedColorValue = text.main as string;
    }

    return {
      background: backgroundValue,
      color: colorValue,
      boxShadow: boxShadowValue,

      "&:hover": {
        backgroundColor: backgroundValue,
        boxShadow: hoveredBoxShadowValue,
      },

      "&:focus:not(:hover)": {
        backgroundColor: focusedBackgroundValue,
        boxShadow: paletteColor
          ? boxShadow([0, 0], [0, 3.2], paletteColor?.main, 0.5)
          : boxShadow([0, 0], [0, 3.2], white.main, 0.5),
      },

      "&:disabled": {
        backgroundColor: backgroundValue,
        color: focusedColorValue,
      },
    };
  };

  const outlinedStyles = () => {
    const paletteColor = palette[color as keyof typeof palette] as PaletteColor;
    const backgroundValue = color === "white" ? rgba(white.main, 0.1) : transparent.main;
    const colorValue = paletteColor?.main ?? white.main;
    const boxShadowValue = paletteColor
      ? boxShadow([0, 0], [0, 3.2], paletteColor?.main, 0.5)
      : boxShadow([0, 0], [0, 3.2], white.main, 0.5);
    let borderColorValue = paletteColor?.main ?? rgba(white.main, 0.75);

    if (color === "white") {
      borderColorValue = rgba(white.main, 0.75);
    }

    return {
      background: backgroundValue,
      color: colorValue,
      borderColor: borderColorValue,

      "&:hover": {
        background: transparent.main,
        borderColor: colorValue,
      },

      "&:focus:not(:hover)": {
        background: transparent.main,
        boxShadow: boxShadowValue,
      },

      "&:active:not(:hover)": {
        backgroundColor: colorValue,
        color: white.main,
        opacity: 0.85,
      },

      "&:disabled": {
        color: colorValue,
        borderColor: colorValue,
      },
    };
  };

  const gradientStyles = () => {
    const gradientColor = gradients[color as keyof typeof gradients];
    const backgroundValue =
      color === "white" || !gradientColor
        ? white.main
        : linearGradient(gradientColor?.main, gradientColor?.state);

    const paletteColor = palette[color as keyof typeof palette] as PaletteColor;
    const boxShadowValue = colored[color as keyof Colored]
      ? `${boxShadow([0, 3], [3, 0], paletteColor?.main, 0.15)}, ${boxShadow(
          [0, 3],
          [1, -2],
          paletteColor?.main,
          0.2
        )}, ${boxShadow([0, 1], [5, 0], paletteColor?.main, 0.15)}`
      : "none";

    const hoveredBoxShadowValue = colored[color as keyof Colored]
      ? `${boxShadow([0, 14], [26, -12], paletteColor?.main, 0.4)}, ${boxShadow(
          [0, 4],
          [23, 0],
          paletteColor?.main,
          0.15
        )}, ${boxShadow([0, 8], [10, -5], paletteColor?.main, 0.2)}`
      : "none";

    let colorValue = white.main;

    if (color === "white") {
      colorValue = text?.main ?? white.main;
    } else if (color === "light") {
      colorValue = gradients.dark.state;
    }

    return {
      background: backgroundValue,
      color: colorValue,
      boxShadow: boxShadowValue,

      "&:hover": {
        boxShadow: hoveredBoxShadowValue,
      },

      "&:focus:not(:hover)": {
        boxShadow: boxShadowValue,
      },

      "&:disabled": {
        background: backgroundValue,
        color: colorValue,
      },
    };
  };

  const textStyles = () => {
    const paletteColor =
      color === "white"
        ? (palette["text" as keyof typeof palette] as PaletteColor)
        : (palette[color as keyof typeof palette] as PaletteColor);
    const colorValue = paletteColor?.main ?? palette.primary.dark;
    const focusedColorValue = paletteColor?.focus ?? white.focus;

    return {
      color: colorValue,

      "&:hover": {
        color: focusedColorValue,
      },

      "&:focus:not(:hover)": {
        color: focusedColorValue,
      },
    };
  };

  const circularStyles = () => ({
    borderRadius: borderRadius.section,
  });

  const iconOnlyStyles = () => {
    let sizeValue = pxToRem(38);

    if (size === "small") {
      sizeValue = pxToRem(25.4);
    } else if (size === "large") {
      sizeValue = pxToRem(52);
    }

    let paddingValue = `${pxToRem(11)} ${pxToRem(11)} ${pxToRem(10)}`;

    if (size === "small") {
      paddingValue = pxToRem(4.5);
    } else if (size === "large") {
      paddingValue = pxToRem(16);
    }

    return {
      width: sizeValue,
      minWidth: sizeValue,
      height: sizeValue,
      minHeight: sizeValue,
      padding: paddingValue,

      "& .material-icons": {
        marginTop: 0,
      },

      "&:hover, &:focus, &:active": {
        transform: "none",
      },
    };
  };

  return {
    ...(variant === "contained" && containedStyles()),
    ...(variant === "outlined" && outlinedStyles()),
    ...(variant === "gradient" && gradientStyles()),
    ...(variant === "text" && textStyles()),
    ...(circular && circularStyles()),
    ...(iconOnly && iconOnlyStyles()),
  };
});
